"use client";

import React, { useEffect, useState, useContext } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Switch,
	FormControlLabel,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import { createMRTColumnHelper } from "material-react-table";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import GenericTable from "../genericTable.js";
import { AuthContext } from "context";
import apiService from "../../services/api-service";
import toast from "react-hot-toast";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { openFileInNewTab } from "utils/navigation";

const ControlliEffettuati = () => {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState([]);
	const [wbsData, setWbsData] = useState([]);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [selectedControl, setSelectedControl] = useState(null);
	const [editData, setEditData] = useState({
		controllo_effettuato: false,
		note: "",
	});
	const authContext = useContext(AuthContext);
	const columnHelper = createMRTColumnHelper();

	const handleSetNotification = (message, type) => {
		if (type === "success") toast.success(message);
		else if (type === "error") toast.error(message);
		else if (type === "warning") toast.warning(message);
	};

	const fetchData = () => {
		setLoading(true);
		Promise.all([
			apiService.get("/controlli-effettuati/", { area_id: localStorage.getItem("areaSelected") }, {}),
			apiService.get(
				"/jobs-management/",
				{
					options: true,
					area_id: localStorage.getItem("areaSelected"),
				},
				{}
			),
		])
			.then(([controlliResponse, wbsResponse]) => {
				setData(controlliResponse);
				// Filter only job options and convert to usable format
				const wbsJobs = (wbsResponse || [])
					.filter((item) => item.id.includes("-job"))
					.map((item) => ({
						id: parseInt(item.id.replace("-job", "")),
						name: item.name,
					}));
				setWbsData(wbsJobs);
			})
			.catch((error) => {
				console.error("Error fetching data:", error);
				handleSetNotification("Errore nel caricamento dei dati", "error");
				setData([]);
				setWbsData([]);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchData();
	}, []);

	const getWbsName = (wbsId) => {
		const wbs = wbsData.find((item) => item.id === wbsId);
		return wbs ? wbs.name : "N/A";
	};

	const handleEdit = (id) => {
		const rowData = data.find((item) => item.id === id);
		if (!rowData) {
			console.error("Row data not found for id:", id);
			return;
		}

		setSelectedControl(rowData);
		setEditData({
			controllo_effettuato: rowData.controllo_effettuato,
			note: rowData.note || "",
		});
		setEditDialogOpen(true);
	};

	const handleSave = async () => {
		try {
			const dataToSend = {
				id: selectedControl.id,
				controllo_effettuato: editData.controllo_effettuato,
				note: editData.note,
			};
			console.log("ciaodnand", dataToSend);
			await apiService.post("/controlli-effettuati/", {}, dataToSend);

			handleSetNotification("Controllo aggiornato con successo", "success");
			setEditDialogOpen(false);
			fetchData();
		} catch (error) {
			console.error("Error updating control:", error);
			handleSetNotification("Errore durante l'aggiornamento del controllo", "error");
		}
	};

	const handleExportPDF = async () => {
		try {
			const areaId = localStorage.getItem("areaSelected");

			// Use fetch directly for binary data (PDF)
			const response = await apiService.post("/controlli-effettuati/export-pdf/", {}, { area_id: areaId });

			console.log("response", response.url);
			openFileInNewTab(response.url);
		} catch (error) {
			console.error("Error exporting PDF:", error);
			handleSetNotification("Errore durante la generazione del PDF", "error");
		}
	};

	const columns = [
		columnHelper.accessor("wbs_id", {
			header: "WBS",
			size: 200,
			enableSorting: true,
			Cell: ({ cell }) => <Typography variant="body2">{getWbsName(cell.getValue())}</Typography>,
		}),
		columnHelper.accessor("controlli", {
			header: "Controlli",
			size: 300,
			Cell: ({ cell }) => {
				const controlli = cell.getValue();
				if (Array.isArray(controlli)) {
					return (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 1,
							}}
						>
							{controlli.map((controllo, index) => (
								<Typography key={index} variant="body2">
									{controllo}
								</Typography>
							))}
						</Box>
					);
				}
				return <Typography variant="body2">{controlli || "N/A"}</Typography>;
			},
		}),
		columnHelper.accessor("riferimenti", {
			header: "Riferimenti",
			size: 200,
			enableSorting: true,
		}),
		columnHelper.accessor("registrazione", {
			header: "Registrazione",
			size: 200,
			enableSorting: true,
		}),
		columnHelper.accessor("applicabile", {
			header: "Applicabile",
			size: 100,
			Cell: ({ cell }) => {
				const isApplicabile = cell.getValue();
				return (
					<Typography
						variant="body2"
						sx={{
							color: isApplicabile ? "green" : "red",
							fontWeight: "bold",
						}}
					>
						{isApplicabile ? "Sì" : "No"}
					</Typography>
				);
			},
		}),
		columnHelper.accessor("controllo_effettuato", {
			header: "Controllo Effettuato",
			size: 150,
			Cell: ({ cell }) => {
				const isEffettuato = cell.getValue();
				return (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{isEffettuato && <CheckCircleIcon sx={{ color: "green", fontSize: 20 }} />}
						<Typography
							variant="body2"
							sx={{
								color: isEffettuato ? "green" : "red",
								fontWeight: "bold",
							}}
						>
							{isEffettuato ? "Sì" : "No"}
						</Typography>
					</Box>
				);
			},
		}),
		columnHelper.accessor("data_controllo", {
			header: "Data Controllo",
			size: 120,
			Cell: ({ cell }) => {
				const date = cell.getValue();
				return date ? new Date(date).toLocaleDateString("it-IT") : "-";
			},
		}),
		columnHelper.accessor("eseguito_da_nome", {
			header: "Eseguito da",
			size: 150,
			Cell: ({ cell, row }) => {
				const nome = cell.getValue();
				const username = row.original.eseguito_da_username;
				return <Typography variant="body2">{nome || username || "-"}</Typography>;
			},
		}),
		columnHelper.accessor("note", {
			header: "Note",
			size: 200,
			Cell: ({ cell }) => {
				const note = cell.getValue();
				return (
					<Typography
						variant="body2"
						sx={{
							wordBreak: "break-word",
							whiteSpace: "pre-wrap",
						}}
					>
						{note || "-"}
					</Typography>
				);
			},
		}),
	];

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box sx={{ padding: "20px" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginBottom: "24px",
					}}
				>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							color: "#333",
							fontSize: "1.75rem",
							display: "flex",
							alignItems: "center",
							gap: "12px",
						}}
					>
						Controlli Effettuati
					</Typography>

					<Button
						variant="contained"
						color="secondary"
						startIcon={<PictureAsPdfIcon />}
						onClick={handleExportPDF}
						sx={{
							textTransform: "none",
							borderRadius: "8px",
							color: "white!important",
						}}
					>
						Esporta PDF
					</Button>
				</Box>

				{loading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "300px",
						}}
					>
						<CircularProgress size={120} sx={{ color: "#2b84ea" }} />
					</Box>
				) : (
					<GenericTable
						authModelName="DAILYACTIVITY"
						data={data}
						columns={columns}
						onEdit={handleEdit}
						sortingKey="wbs_id"
						isAddRowEnabled={false}
						disableButton={true}
					/>
				)}

				{/* Edit Dialog */}
				<Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
					<DialogTitle>Modifica Controllo</DialogTitle>
					<DialogContent>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 3,
								mt: 2,
							}}
						>
							<FormControlLabel
								control={
									<Switch
										checked={editData.controllo_effettuato}
										onChange={(e) =>
											setEditData({
												...editData,
												controllo_effettuato: e.target.checked,
											})
										}
										color="primary"
									/>
								}
								label="Controllo Effettuato"
							/>

							<TextField
								label="Note"
								value={editData.note}
								onChange={(e) =>
									setEditData({
										...editData,
										note: e.target.value,
									})
								}
								fullWidth
								multiline
								rows={3}
								variant="outlined"
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setEditDialogOpen(false)}>Annulla</Button>
						<Button onClick={handleSave} variant="contained" sx={{ color: "white!important" }}>
							Salva
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</DashboardLayout>
	);
};

export default ControlliEffettuati;
